import { IAgentRuntime, Memory, Provider, ProviderResult, State, elizaLogger } from '@elizaos/core';
import { ZapperConfig, PortfolioData, ZapperResponse } from '../types';

export const portfolioProvider: Provider = {
  name: 'portfolioProvider',
  description: 'Provides portfolio data for wallet addresses using Zapper API',
  dynamic: true,
  get: async (runtime: IAgentRuntime, message: Memory, state: State): Promise<ProviderResult> => {
    elizaLogger.debug('portfolioProvider::get');

    const zapperConfig: ZapperConfig = {
      apiKey: runtime.getSetting('ZAPPER_API_KEY'),
      apiUrl: runtime.getSetting('ZAPPER_API_URL') || 'https://public.zapper.xyz/graphql',
    };

    if (!zapperConfig.apiKey) {
      elizaLogger.error('ZAPPER_API_KEY not configured');
      return {
        text: 'Portfolio data unavailable - API key not configured',
        data: { error: 'API key required' },
      };
    }

    // Extract wallet address from message or state
    const walletAddress = extractWalletAddress(message.content.text);

    if (!walletAddress) {
      elizaLogger.debug(
        'portfolioProvider: No wallet address found in message, returning neutral context'
      );
      return {
        text: 'Portfolio data context available when wallet address is provided',
        data: { provider: 'portfolioProvider', status: 'neutral' },
      };
    }

    try {
      const portfolioData = await fetchPortfolioData(zapperConfig, walletAddress);

      if (!portfolioData) {
        return {
          text: 'No portfolio data found for this address',
          data: { address: walletAddress },
        };
      }

      const summary = `Portfolio for ${walletAddress}:
Total Value: $${portfolioData.totalValueUSD.toFixed(2)}
Tokens: ${portfolioData.tokenBalances.length}
Networks: ${portfolioData.networks.join(', ')}`;

      return {
        text: summary,
        data: {
          address: walletAddress,
          portfolio: portfolioData,
        },
        values: {
          totalValueUSD: portfolioData.totalValueUSD,
          tokenCount: portfolioData.tokenBalances.length,
          networkCount: portfolioData.networks.length,
        },
      };
    } catch (error) {
      elizaLogger.error('portfolioProvider: Error fetching portfolio data:', {
        error: error instanceof Error ? error.message : String(error),
        address: walletAddress,
        apiUrl: zapperConfig.apiUrl,
        hasApiKey: !!zapperConfig.apiKey,
      });
      return {
        text: 'Portfolio data temporarily unavailable',
        data: {
          provider: 'portfolioProvider',
          status: 'error',
          error: error instanceof Error ? error.message : String(error),
          address: walletAddress,
        },
      };
    }
  },
};

function extractWalletAddress(text: string | undefined): string | null {
  // Simple regex for Ethereum addresses
  if (!text) return null;
  const ethAddressRegex = /0x[a-fA-F0-9]{40}/;
  const match = text.match(ethAddressRegex);
  return match ? match[0] : null;
}

async function fetchPortfolioData(
  config: ZapperConfig,
  address: string
): Promise<PortfolioData | null> {
  // Map network names to chain IDs (recommended by Zapper API)
  const chainIds = [1, 137, 42161, 10, 8453]; // ethereum, polygon, arbitrum, optimism, base

  const query = `
    query PortfolioV2($addresses: [Address!]!, $chainIds: [Int!]) {
      portfolioV2(addresses: $addresses, chainIds: $chainIds) {
        tokenBalances {
          byToken {
            edges {
              node {
                balance
                balanceUSD
                symbol
                name
                network {
                  name
                }
                tokenAddress
              }
            }
          }
        }
      }
    }
  `;

  const variables = {
    addresses: [address],
    chainIds: chainIds,
  };

  elizaLogger.debug('Portfolio Provider API request:', {
    url: config.apiUrl,
    variables,
    chainIds: chainIds,
  });

  const response = await fetch(config.apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-zapper-api-key': config.apiKey,
    },
    body: JSON.stringify({
      query,
      variables,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    elizaLogger.error('portfolioProvider: API HTTP error:', {
      status: response.status,
      statusText: response.statusText,
      error: errorText,
      url: config.apiUrl,
      address: address,
      chainIds: chainIds,
    });
    throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
  }

  const result = (await response.json()) as {
    data?: {
      portfolioV2?: {
        tokenBalances?: {
          byToken?: {
            edges?: Array<{
              node: {
                balance: string;
                balanceUSD: number;
                symbol: string;
                name: string;
                network?: { name: string };
                tokenAddress: string;
              };
            }>;
          };
        };
      };
    };
    errors?: Array<{ message: string; locations?: Array<{ line: number; column: number }> }>;
  };

  if (result.errors) {
    elizaLogger.error('portfolioProvider: API GraphQL errors:', {
      errors: result.errors,
      query,
      variables,
      url: config.apiUrl,
      address: address,
    });
    throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);
  }

  // Check if we have valid portfolio data
  if (!result.data?.portfolioV2?.tokenBalances?.byToken?.edges) {
    return null;
  }

  const tokenBalances =
    result.data?.portfolioV2?.tokenBalances?.byToken?.edges?.map((edge) => ({
      symbol: edge.node.symbol,
      name: edge.node.name,
      balance: edge.node.balance,
      balanceUSD: edge.node.balanceUSD,
      network: edge.node.network?.name || 'unknown',
      address: edge.node.tokenAddress,
    })) || [];

  const totalValueUSD = tokenBalances.reduce((sum: number, token) => sum + token.balanceUSD, 0);

  const networks: string[] = [...new Set(tokenBalances.map((token) => token.network))].filter(
    (network): network is string => typeof network === 'string'
  );

  return {
    totalValueUSD,
    tokenBalances,
    networks,
  };
}
